import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, Caption1, IconButton, ListItem, TrashIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { getLoggedUser } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { removeExtensionFromFileName } from "~/utils/files";
import { ConfirmAction } from "../ActionElements";
import UserNameContainer from "../Users/UserNameContainer";

export const ExpenseAttachmentWrapper = ({
  children,
  name,
  sizeValue,
  id,
  onDelete,
  onOpenPreview,
  createdById,
  onRemove,
  canEdit,
  extension,
}) => {
  const { t } = useTranslation();
  const loggedUser = useSelector(getLoggedUser);

  const fileName = removeExtensionFromFileName(name, extension);

  const handleOpenPreview = useCallback(() => {
    typeof onOpenPreview === "function" && onOpenPreview(id);
  }, [id, onOpenPreview]);

  const handleRemove = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  return (
    <ListItem
      key={id}
      className="tw-flex tw-items-center tw-py-1 tw-gap-3"
    >
      <div
        className="tw-flex tw-items-center tw-min-w-0 tw-w-full"
        onClick={handleOpenPreview}
      >
        <div className="tw-w-12 tw-h-12 tw-rounded-md tw-shrink-0 tw-flex tw-items-center tw-justify-center">
          {children}
        </div>
        <div className="tw-ml-3 tw-min-w-0">
          <Body2 className="tw-flex tw-items-center tw-max-w-full">
            <div className="tw-truncate">{extension?.length > 0 ? `${fileName}.` : fileName}</div>
            {extension ? <div className="tw-shrink-0">{extension}</div> : null}
          </Body2>
          <Caption1
            color="tertiary"
            className="tw-truncate"
          >
            {createdById ? (
              <UserNameContainer
                userId={createdById}
                shortName
              />
            ) : (
              loggedUser.get("short_display_name")
            )}
            {sizeValue > 0 ? `, ${Angie.functions.format_file_size(sizeValue)}` : ", --"}
          </Caption1>
        </div>
      </div>
      {typeof onRemove === "function" ? (
        <IconButton
          className="tw-ml-auto tw-shrink-0"
          type="button"
          variant="text gray"
          onClick={handleRemove}
        >
          <TrashIcon />
        </IconButton>
      ) : null}
      {typeof onDelete === "function" ? (
        <ConfirmAction
          title={t("Delete Attachment")}
          content={t("Deleting this attachment will remove it permanently - you won't be able to undo this.")}
          confirmBtnText={t("Delete")}
        >
          <IconButton
            className="tw-ml-auto tw-shrink-0"
            type="button"
            variant="text gray"
            onClick={handleDelete}
          >
            <TrashIcon />
          </IconButton>
        </ConfirmAction>
      ) : null}
      {!onRemove && !onDelete && canEdit ? (
        <IconButton
          className="tw-ml-auto tw-shrink-0"
          type="button"
          variant="text gray"
          disabled
        >
          <TrashIcon />
        </IconButton>
      ) : null}
    </ListItem>
  );
};

ExpenseAttachmentWrapper.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  extension: PropTypes.string,
  onDelete: PropTypes.func,
  onRemove: PropTypes.func,
  onOpenPreview: PropTypes.func,
  sizeValue: PropTypes.number,
  children: PropTypes.node,
  createdById: PropTypes.number,
  canEdit: PropTypes.bool,
};
